import { useMemo, useState } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Box, Button, Chip, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import { BranchSummary, createBranch, listBranches, updateBranch } from '../../api/branches';
import { TableToolbar } from '../../components/table/TableToolbar';
import { PageHeader } from '../../components/PageHeader';
import { BranchFormDialog, BranchFormValues } from './BranchFormDialog';

export const BranchesPage = () => {
  const queryClient = useQueryClient();
  const branchesQuery = useQuery({ queryKey: ['branches'], queryFn: () => listBranches() });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState<BranchSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['branches'] });

  const createMutation = useMutation({
    mutationFn: (values: BranchFormValues) =>
      createBranch({
        ...values,
        address: values.address || undefined,
        city: values.city || undefined,
        country: values.country || undefined,
        phone: values.phone || undefined,
        email: values.email || undefined,
      }),
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      setError(null);
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, values }: { id: number; values: BranchFormValues }) =>
      updateBranch(id, {
        ...values,
        address: values.address || undefined,
        city: values.city || undefined,
        country: values.country || undefined,
        phone: values.phone || undefined,
        email: values.email || undefined,
      }),
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      setError(null);
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const columns: GridColDef<BranchSummary>[] = useMemo(
    () => [
      { field: 'code', headerName: 'Code', width: 100 },
      { field: 'name', headerName: 'Name', flex: 1 },
      { field: 'city', headerName: 'City', width: 140 },
      { field: 'country', headerName: 'Country', width: 140 },
      {
        field: 'isActive',
        headerName: 'Status',
        width: 110,
        renderCell: (params) => (
          <Chip size="small" label={params.value ? 'Active' : 'Inactive'} color={params.value ? 'success' : 'default'} />
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => {
                setEditingBranch(params.row);
                setError(null);
                setDialogOpen(true);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [],
  );

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={ApartmentOutlinedIcon}
        color="#8354C6"
        title="Branches"
        subtitle="Manage office locations and contact details."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingBranch(null);
              setError(null);
              setDialogOpen(true);
            }}
          >
            New branch
          </Button>
        }
      />

      <Box sx={{ height: 520, bgcolor: 'background.paper', borderRadius: 1 }}>
        <DataGrid
          rows={branchesQuery.data ?? []}
          columns={columns}
          loading={branchesQuery.isLoading}
          slots={{ toolbar: TableToolbar }}
          disableRowSelectionOnClick
        />
      </Box>

      <BranchFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        editingBranch={editingBranch}
        errorMessage={error}
        onSubmit={async (values) => {
          if (editingBranch) {
            await updateMutation.mutateAsync({ id: editingBranch.id, values });
          } else {
            await createMutation.mutateAsync(values);
          }
        }}
      />
    </Box>
  );
};

const extractErrorMessage = (err: unknown): string => {
  if (isAxiosError(err)) {
    return err.response?.data?.message ?? 'Something went wrong.';
  }
  return 'Something went wrong.';
};
